import { useEffect, useState } from "react";
import { FaRegStar, FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { defaultAxiosInstance } from "../api/defaultInstance";
import "../css/AdProductManagement.css"; // 스타일 분리
import Layout from "./Layout";

function AdProductManagement() {
  // 상품 목록
  const [products, setProducts] = useState([]);
  // 페이징 처리
  const [totalCount, setTotalCount] = useState(0);
  // 필터 상태
  const [filter, setFilter] = useState({
    metaProduct: "reward", // 메타상품 기본값
    productType: "", // 상품유형
    advertiserName: "", // 시행사
    page: 1,
    limit: 20,
  });

  // 초기 조회
  useEffect(() => {
    fetchProducts();
  }, []);

  const navigate = useNavigate();

  // 광고 상품 조회 API
  const fetchProducts = async (currentFilter = filter) => {
    try {
      const response = await defaultAxiosInstance.get(
        "/advertisement/admin/products",
        {
          params: {
            ...currentFilter,
            type: currentFilter.metaProduct, // metaProduct 값을 type으로 전송
          },
        },
      );
      const result = response.data.data;
      setProducts(result.products || []);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      console.error("광고 상품 조회 오류", error);
    }
  };

  // 필터 변경
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilter = { ...filter, [name]: value };
    setFilter(newFilter);

    // metaProduct가 변경되면 즉시 API 호출
    if (name === "metaProduct") {
      fetchProducts(newFilter);
    }
  };

  // 검색 버튼 클릭 시 필터 적용
  const applyFilter = () => {
    const newFilter = { ...filter, page: 1 };
    setFilter(newFilter);
    fetchProducts(newFilter);
  };

  // 페이징: 이전 페이지
  const handlePrevPage = () => {
    if (filter.page > 1) {
      const newFilter = { ...filter, page: filter.page - 1 };
      setFilter(newFilter);
      fetchProducts(newFilter);
    }
  };

  // 페이징: 다음 페이지
  const handleNextPage = () => {
    const maxPage = Math.ceil(totalCount / filter.limit);
    if (filter.page < maxPage) {
      const newFilter = { ...filter, page: filter.page + 1 };
      setFilter(newFilter);
      fetchProducts(newFilter);
    }
  };

  // 시작 인덱스와 끝 인덱스
  const startIndex = (filter.page - 1) * filter.limit + 1;
  const endIndex = Math.min(filter.page * filter.limit, totalCount);

  // 가격 표시 형식 (예: 30000 -> "3만")
  const formatPrice = (price) => {
    if (price >= 10000) {
      // 만 단위로 변환
      return `${price / 10000}만원`;
    }
    return `${price}원`;
  };

  /**
   * 추천(별) 클릭 시 동작
   * - 클릭 전 빈 별 => 클릭 후 꽉 찬 별
   * - 클릭 전 꽉 찬 별 => 클릭 후 빈 별
   */
  const handleStarClick = async (product, e) => {
    // 별 클릭 시 행 전체 클릭 방지를 위해 이벤트 전파 중지
    e.stopPropagation();

    // 현재 isRecommended가 1이면 => false (0)
    // 0이면 => true (1)
    const newRecommended = product.isRecommended ? false : true;

    try {
      await defaultAxiosInstance.put("/advertisement/admin/recommendation", {
        adProductId: product.adProductId,
        isRecommended: newRecommended,
      });
      // API 성공 시, 로컬 상태 업데이트
      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p.adProductId === product.adProductId
            ? { ...p, isRecommended: newRecommended ? 1 : 0 }
            : p,
        ),
      );
    } catch (error) {
      console.error("추천 설정 오류", error);
    }
  };

  // 테이블 행 클릭 시 상세 화면으로 이동 (adProductId 전달)
  const handleRowClick = (product) => {
    navigate(`/ads/product/detail?adProductId=${product.adProductId}`, {
      state: { metaProduct: filter.metaProduct },
    });
  };

  const handleRegisterClick = () => {
    navigate("/ads/product/write", {
      state: { metaProduct: filter.metaProduct },
    });
  };

  return (
    <Layout>
      <div className="ad-product-management-container">
        {/* 필터 영역 */}
        <div className="filter-bar-2">
          <div className="filter-group">
            <label>메타상품</label>
            <select
              name="metaProduct"
              onChange={handleFilterChange}
              value={filter.metaProduct}
            >
              <option value="reward">리워드</option>
              <option value="review_delete">악성 리뷰</option>
              <option value="blog_press_corps">블로그 기자단</option>
              <option value="news_release">언론기사 배포</option>
            </select>
          </div>
          {filter.metaProduct === "reward" && (
            <>
              <div className="filter-group">
                <label>상품유형</label>
                <select
                  name="productType"
                  onChange={handleFilterChange}
                  value={filter.productType}
                >
                  <option value="">선택</option>
                  <option value="유입">유입</option>
                  <option value="저장">저장</option>
                  <option value="저장+유입">저장+유입</option>
                </select>
              </div>
              <div className="filter-group">
                <label>시행사</label>
                <select
                  name="advertiserName"
                  onChange={handleFilterChange}
                  value={filter.advertiserName}
                >
                  <option value="">선택</option>
                  <option value="버즈빌">버즈빌</option>
                  <option value="호올스">호올스</option>
                  <option value="토스">토스</option>
                  <option value="멘토스">멘토스</option>
                  <option value="길찾기">길찾기</option>
                  <option value="당근">당근</option>
                  <option value="베이직">베이직</option>
                  <option value="브라보">브라보</option>
                  <option value="찰리">찰리</option>
                  <option value="제주">제주</option>
                  <option value="투플">투플</option>
                  <option value="홈런볼">기프트</option>
                  <option value="최적화">최적화</option>
                  <option value="핫팩">핫팩</option>
                  <option value="카카오">카카오</option>
                </select>
              </div>
              <div className="filter-button-wrapper">
                <br></br>
                <button onClick={applyFilter}>검색</button>
              </div>
            </>
          )}
        </div>

        {/* 헤더 영역 */}
        <div className="table-header">
          <h3 className="section-title">광고 상품 관리</h3>
          <div className="button-group">
            <button
              type="button"
              className="register-btn"
              onClick={handleRegisterClick}
            >
              광고 상품 등록하기
            </button>
          </div>
        </div>

        {/* 상품 테이블 */}
        <div className="ad-product-table">
          <table>
            <thead>
              <tr>
                {filter.metaProduct === "reward" && (
                  <>
                    <th>시행사</th>
                    <th>상품타입</th>
                    <th>상품명</th>
                    <th>노출구좌</th>
                    <th>단가</th>
                    <th>최소수량</th>
                    <th>최소기간</th>
                    <th>최대기간</th>
                    <th>추천</th>
                  </>
                )}
                {filter.metaProduct === "review_delete" && (
                  <>
                    <th>상품명</th>
                    <th>단가</th>
                  </>
                )}
                {filter.metaProduct === "news_release" && (
                  <>
                    <th>언론사 명</th>
                    <th>언론사 URL</th>
                    <th>카테고리 리스트</th>
                    <th>송출 매체</th>
                    <th>시작 금액</th>
                    <th>대필 여부</th>
                    <th>추천</th>
                  </>
                )}
                {filter.metaProduct === "blog_press_corps" && (
                  <th>기획 진행중</th>
                )}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                if (filter.metaProduct === "reward") {
                  const priceString = `${formatPrice(p.price)}/${p.unit}`;
                  const minQuantityString = `${p.minQuantity}건`;
                  const minPeriodString = `${p.minPeriod}일`;
                  const maxPeriodString = `${p.maxPeriod}일`;
                  const isRecommended = p.isRecommended === 1;

                  return (
                    <tr
                      key={p.adProductId}
                      onClick={() => handleRowClick(p)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{p.advertiserName === "홈런볼" ? "기프트" : p.advertiserName}</td>
                      <td>{p.productType}</td>
                      <td>{p.adName}</td>
                      <td>{p.exposurePosition[0]}</td>
                      <td>{priceString}</td>
                      <td>{minQuantityString}</td>
                      <td>{minPeriodString}</td>
                      <td>{maxPeriodString}</td>
                      <td style={{ cursor: "pointer" }}>
                        {isRecommended ? (
                          <FaStar
                            style={{ color: "gold" }}
                            onClick={(e) => handleStarClick(p, e)}
                          />
                        ) : (
                          <FaRegStar
                            style={{ color: "gold" }}
                            onClick={(e) => handleStarClick(p, e)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                } else if (filter.metaProduct === "review_delete") {
                  return (
                    <tr
                      key={p.adProductId}
                      onClick={() => handleRowClick(p)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{p.adName}</td>
                      <td>{formatPrice(p.price)}</td>
                    </tr>
                  );
                } else if (filter.metaProduct === "news_release") {
                  return (
                    <tr
                      key={p.adProductId}
                      onClick={() => handleRowClick(p)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{p.mediaName}</td>
                      <td>
                        <a
                          href={p.mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {p.mediaUrl}
                        </a>
                      </td>
                      <td>
                        {Array.isArray(p.categories)
                          ? p.categories.map((c) => c.name).join(", ")
                          : ""}
                      </td>
                      <td>
                        {Array.isArray(p.exposureMedia)
                          ? p.exposureMedia.join(", ")
                          : ""}
                      </td>
                      <td>{p.startPrice?.toLocaleString()}원</td>
                      <td>{p.isGhostwriting === 1 ? "Y" : "N"}</td>
                      <td style={{ cursor: "pointer" }}>
                        {p.isRecommended === 1 ? (
                          <FaStar
                            style={{ color: "gold" }}
                            onClick={(e) => handleStarClick(p, e)}
                          />
                        ) : (
                          <FaRegStar
                            style={{ color: "gold" }}
                            onClick={(e) => handleStarClick(p, e)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                } else {
                  return (
                    <tr>
                      <td colSpan="1" style={{ textAlign: "center" }}>
                        기획 진행중입니다.
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>

        {/* 페이징 영역 */}
        <div className="pagination-info">
          <div>
            총 {totalCount}개 항목 중 {startIndex} - {endIndex}
          </div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={filter.page === 1}>
              이전
            </button>
            <button className="active">{filter.page}</button>
            <button onClick={handleNextPage} disabled={endIndex >= totalCount}>
              다음
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AdProductManagement;
